import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    createDesignation,
    getDesignationDetail,
    updateDesignation,
} from "../../../../services/workspace/designation.service";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useFormik } from "formik";
import DefaultInput from "../../inputs/defaultInput";
import DefaultTextarea from "../../inputs/defaultTextarea";
import DefaultButton from "../../buttons/defaultButton";
import DefaultSearchSelectInput from "../../inputs/defaultSearchSelectInput";
import Loading from "../../skeleton/loading";
import { _transformPayload, convertToLabelValue } from "../../../../utils/common";
import { useDispatch, useSelector } from "react-redux";
import { _fetchDepartments, _fetchDesignations, _fetchRoles } from "../../../../store/actions/configuration";
import { _updateDesignation } from "../../../../store/slices/baseSetup.slice";
import DefaultAlert from "../../alert/defaultAlert";

const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required."),
    abrv: Yup.string().trim().required("Abbreviation is required."),
    description: Yup.string().trim(),
    department_id: Yup.object()
        .required("Department is required.")
        .nullable()
        .test("isValid", "Department is required.", (value) => {
            return value && value.value && value.label;
        }),
    roles: Yup.array()
        .of(Yup.object().required("Roles is required"))
        .required("Roles are required")
        .min(1, "At least one Role must be selected"),
});

const AddDesignationForm = ({ isModal = false, closeModal = () => null }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { pathname } = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState();
    const dispatch = useDispatch();
    const { departments, roles } = useSelector((state) => state.basicSetup)

    const fetchDetail = () => {
        if (!pathname.includes("manage-designations")) return;
        setLoading(true)
        getDesignationDetail(id)
            .then((response) => {
                setLoading(false);
                setData(response);
            })
            .catch((error) => {
                toast.error("Failed to fetch the designation data!");
            });
    };

    useEffect(() => {
        dispatch(_fetchDepartments())
        dispatch(_fetchRoles())
        if (!id) return
        fetchDetail();
    }, [id]);

    const {
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
        values,
        touched,
        errors,
    } = useFormik({
        initialValues: {
            name: data?.name || "",
            abrv: data?.abrv || "",
            description: data?.description || "",
            department_id: data?.department ? { label: data.department.name, value: data.department.id } : "",
            roles: data?.roles && data?.roles?.length ? convertToLabelValue(data?.roles) : [],
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            setIsSubmitting(true);
            const payload = _transformPayload(values);
            const isModuleId = pathname.includes("manage-designations");
            if (id && isModuleId) {
                updateDesignation(payload, id)
                    .then((response) => {
                        toast.success("Designation updated successfully!");
                        navigate("/configuration/manage-designations");
                    })
                    .catch((error) => {
                        setIsSubmitting(false);
                        toast.error(
                            "Failed to update designation!" + error.response.data?.message
                        );
                    });
            } else {
                createDesignation(payload)
                    .then((response) => {
                        if (isModal) {
                            if (response) {
                                const payalod = { label: response?.name, value: response?.id, ...response }
                                dispatch(_updateDesignation(payalod))
                            } else {
                                dispatch(_fetchDesignations())
                            }
                            closeModal()
                        } else {
                            toast.success("Designation created successfully!");
                            navigate("/configuration/manage-designations");
                        }
                    })
                    .catch((error) => {
                        setIsSubmitting(false);
                        toast.error(
                            "Failed to create designation!" + error.response.data?.message
                        );
                    });
            }
        },
    });

    return (
        <>
            {loading ? (
                <Loading />
            ) : (
                <div className={`bg-white ${isModal ? "p-0" : "p-4"} flex flex-col gap-4`}>
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md">
                        <p className="text-sm font-medium">
                            <span className="font-bold">Note:</span> The
                            <span className="font-bold text-yellow-900"> Permissoins </span>
                            define which modules and sections are visible on a user's dashboard. Please assign them carefully.
                            <span className='text-blue-500 pl-1 text-sm'>
                                <a
                                    href="/configuration/manage-roles"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 font-medium transition-colors duration-200"
                                >
                                    Click to explore the documentation.
                                </a>
                            </span>
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className={`grid gap-4 gap-y-2 ${isModal ? "grid-cols-2" : "grid-cols-4"}`}>
                            <DefaultInput
                                label={"Designation Name"}
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.name && errors.name ? errors.name : ""}
                                required
                            />
                            <DefaultInput
                                label={"Abbreviation"}
                                name="abrv"
                                value={values.abrv}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.abrv && errors.abrv ? errors.abrv : ""}
                                required
                            />
                            <DefaultSearchSelectInput
                                label={"Permissions"}
                                name="roles"
                                value={values.roles}
                                isMulti
                                options={roles}
                                onChange={(value) => {
                                    setFieldValue("roles", value)
                                }}
                                onBlur={handleBlur}
                                error={
                                    touched.roles && errors.roles
                                        ? errors.roles
                                        : ""
                                }
                                required
                            />
                            <DefaultSearchSelectInput
                                label={"Departments"}
                                name="department_id"
                                value={values.department_id}
                                options={departments}
                                onChange={(value) => {
                                    setFieldValue("department_id", value)
                                }}
                                onBlur={handleBlur}
                                error={
                                    touched.department_id && errors.department_id
                                        ? errors.department_id
                                        : ""
                                }
                                required
                            />
                            <div className={isModal ? "col-span-2" : "col-span-4"}>
                                <DefaultTextarea
                                    label={"Description"}
                                    placeholder={"Write Description here..."}
                                    name="description"
                                    value={values.description}
                                    onChange={(handleChange)}
                                    onBlur={handleBlur}
                                    error={
                                        touched.description && errors.description
                                            ? errors.description
                                            : ""
                                    }

                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <DefaultButton
                                variant="default"
                                name={"Submit"}
                                type="submit"
                                loading={isSubmitting}
                            />
                            {isModal ?
                                <DefaultButton
                                    variant="secondary"
                                    name={"Close"}
                                    onClick={closeModal}
                                /> : null}
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}

export default AddDesignationForm
